import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Section, Heading, Spinner, EmptyState } from "../components/Bits";
import PageHero from "../components/PageHero";
import ImageUpload from "../components/ImageUpload";
import { Trash2, MapPin } from "lucide-react";
import { Link } from "react-router-dom";

export default function Journal() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ plant_name: "", notes: "", image_url: "", lat: "", lng: "" });
  const { user } = useAuth() || {};

  const load = () => {
    setLoading(true);
    api.get("/journal").then((r) => setEntries(r.data || [])).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (user) load();
    else setLoading(false);
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/journal", {
        ...form,
        lat: form.lat ? parseFloat(form.lat) : null,
        lng: form.lng ? parseFloat(form.lng) : null,
        found_at: new Date().toISOString(),
      });
      setForm({ plant_name: "", notes: "", image_url: "", lat: "", lng: "" });
      toast.success("Fund gespeichert");
      load();
    } catch {
      toast.error("Speichern fehlgeschlagen");
    }
  };

  const useGps = () => {
    if (!navigator.geolocation) return toast.error("GPS nicht verfügbar");
    navigator.geolocation.getCurrentPosition(
      (pos) => setForm((f) => ({ ...f, lat: pos.coords.latitude.toFixed(6), lng: pos.coords.longitude.toFixed(6) })),
      () => toast.error("Standort nicht abrufbar")
    );
  };

  const del = async (id) => {
    if (!confirm("Wirklich löschen?")) return;
    await api.delete(`/journal/${id}`);
    load();
  };

  if (!user) {
    return (
      <Section>
        <PageHero slug="journal" variant="soft"
        defaultOverline="Tagebuch"
        defaultTitle="Deine Pflanzenfunde"
        defaultSubtitle=""
      />
        <EmptyState title="Bitte anmelden" description="Dein Tagebuch ist privat – melde dich an, um Funde zu speichern.">
          <Link to="/login" className="btn-primary">Zum Login</Link>
        </EmptyState>
      </Section>
    );
  }

  return (
    <Section>
      <Heading overline="Tagebuch" title="Deine Pflanzenfunde" subtitle="Notiere wann und wo du welche Pflanze gefunden hast – mit Foto und GPS." />

      <div className="grid lg:grid-cols-3 gap-8">
        <form onSubmit={submit} className="plant-card p-6 space-y-3" data-testid="journal-form">
          <h3 className="font-display text-xl">Neuer Fund</h3>
          <input required placeholder="Pflanzenname" value={form.plant_name} onChange={(e) => setForm({ ...form, plant_name: e.target.value })} className="w-full px-4 py-3 rounded-xl border" style={{ borderColor: "var(--wp-border)" }} data-testid="journal-name" />
          <textarea placeholder="Notizen" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-4 py-3 rounded-xl border min-h-[80px]" style={{ borderColor: "var(--wp-border)" }} data-testid="journal-notes" />
          <div>
            <div className="overline mb-2">Foto</div>
            <ImageUpload value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} testId="journal-image" />
          </div>
          <div className="flex gap-2">
            <input placeholder="Lat" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} className="flex-1 px-4 py-3 rounded-xl border" style={{ borderColor: "var(--wp-border)" }} />
            <input placeholder="Lng" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} className="flex-1 px-4 py-3 rounded-xl border" style={{ borderColor: "var(--wp-border)" }} />
          </div>
          <button type="button" onClick={useGps} className="btn-secondary w-full justify-center"><MapPin className="w-4 h-4" /> GPS verwenden</button>
          <button className="btn-primary w-full justify-center" data-testid="journal-submit">Eintrag speichern</button>
        </form>

        <div className="lg:col-span-2">
          {loading ? <Spinner /> : entries.length === 0 ? (
            <EmptyState title="Noch keine Einträge" description="Dein erster Fund wartet draußen!" />
          ) : (
            <div className="space-y-4" data-testid="journal-list">
              {entries.map((e) => (
                <div key={e.id} className="plant-card p-5 flex gap-4 items-start" data-testid={`journal-entry-${e.id}`}>
                  {e.image_url && <img src={e.image_url} alt={e.plant_name} className="w-20 h-20 object-cover rounded-lg" />}
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div className="font-display text-lg">{e.plant_name}</div>
                      <button onClick={() => del(e.id)} className="opacity-50 hover:opacity-100" title="Löschen"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="text-xs opacity-60">{new Date(e.created_at).toLocaleString("de-DE")}</div>
                    {e.notes && <p className="text-sm mt-2">{e.notes}</p>}
                    {(e.lat && e.lng) && <div className="text-xs mt-2 opacity-70"><MapPin className="w-3 h-3 inline" /> {e.lat.toFixed(4)}, {e.lng.toFixed(4)}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Section>
  );
}
